import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Briefcase, Shield, FileText, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function TermsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const userType = new URLSearchParams(location.search).get('type') || 'trainer';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="terms-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors mr-4"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <span className="font-outfit font-bold text-xl text-slate-900">Training Solutions</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Title */}
          <div className="bg-slate-900 text-white px-8 py-6">
            <div className="flex items-center gap-3 mb-2">
              <Shield className="w-6 h-6" />
              <h1 className="font-outfit text-2xl font-bold">
                {userType === 'corporate' ? 'Corporate' : 'Trainer'} Service Agreement
              </h1>
            </div>
            <p className="text-slate-300 text-sm">
              Training Solutions Platform - Terms & Conditions
            </p>
          </div>

          {/* Content */}
          <div className="px-8 py-6 prose prose-slate max-w-none">
            <p className="text-sm text-slate-500 mb-6">
              Effective Date: January 2025 | Last Updated: January 2025
            </p>

            <h2 className="font-outfit text-lg font-semibold text-slate-900 mt-6 mb-3">
              1. INTRODUCTION
            </h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              This Service Agreement ("Agreement") is entered into between Training Solutions ("Platform", "We", "Us") 
              and the {userType === 'corporate' ? 'Corporate Entity' : 'Trainer'} ("You", "User") upon registration on the Platform.
              By creating an account, you agree to be bound by these terms and conditions.
            </p>

            <h2 className="font-outfit text-lg font-semibold text-slate-900 mt-6 mb-3">
              2. PLATFORM SERVICES
            </h2>
            <p className="text-slate-600 text-sm leading-relaxed mb-3">
              Training Solutions provides a comprehensive training management platform offering:
            </p>
            <ul className="text-slate-600 text-sm space-y-2 list-disc list-inside">
              <li>Trainer search and discovery based on skills, experience, and qualifications</li>
              <li>Smart matching of trainers with training requirements and criteria</li>
              <li>Calendar-based availability management and booking system</li>
              <li>Secure payment processing and management</li>
              <li>Training session feedback and rating system</li>
              <li>Contract generation and documentation</li>
            </ul>

            {userType === 'trainer' ? (
              <>
                <h2 className="font-outfit text-lg font-semibold text-slate-900 mt-6 mb-3">
                  3. TRAINER OBLIGATIONS
                </h2>
                <p className="text-slate-600 text-sm leading-relaxed mb-3">As a registered Trainer, you agree to:</p>
                <ul className="text-slate-600 text-sm space-y-2 list-disc list-inside">
                  <li>Provide accurate and complete profile information including qualifications and experience</li>
                  <li>Maintain updated availability on your calendar</li>
                  <li>Honor all confirmed bookings and arrive prepared for training sessions</li>
                  <li>Deliver training services professionally and to the best of your abilities</li>
                  <li>Not solicit clients directly outside the Platform for 12 months after initial contact</li>
                  <li>Maintain confidentiality of corporate information shared during engagements</li>
                </ul>

                <h2 className="font-outfit text-lg font-semibold text-slate-900 mt-6 mb-3">
                  4. PAYMENT TERMS FOR TRAINERS
                </h2>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
                  <h3 className="font-semibold text-emerald-900 mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Back-to-Back Payment Arrangement
                  </h3>
                  <ul className="text-emerald-800 text-sm space-y-1 list-disc list-inside">
                    <li>Payment will be credited to your registered account within <strong>7 (seven) working days</strong> of receipt of payment from the Corporate</li>
                    <li>Platform commission: 10% of the training fee</li>
                    <li>Payment is processed only after successful completion of the training session</li>
                    <li>Trainer must submit completion confirmation within 48 hours of session end</li>
                  </ul>
                </div>
              </>
            ) : (
              <>
                <h2 className="font-outfit text-lg font-semibold text-slate-900 mt-6 mb-3">
                  3. CORPORATE OBLIGATIONS
                </h2>
                <p className="text-slate-600 text-sm leading-relaxed mb-3">As a registered Corporate, you agree to:</p>
                <ul className="text-slate-600 text-sm space-y-2 list-disc list-inside">
                  <li>Provide accurate company information and training requirements</li>
                  <li>Make timely payments as per the agreed training contract</li>
                  <li>Provide necessary facilities and resources for training sessions</li>
                  <li>Communicate any changes or cancellations at least 48 hours in advance</li>
                  <li>Provide honest feedback after each training session</li>
                  <li>Not directly engage trainers outside the Platform for 12 months after initial contact</li>
                </ul>

                <h2 className="font-outfit text-lg font-semibold text-slate-900 mt-6 mb-3">
                  4. PAYMENT TERMS FOR CORPORATES
                </h2>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Payment Schedule
                  </h3>
                  <ul className="text-blue-800 text-sm space-y-1 list-disc list-inside">
                    <li>100% payment must be made before the scheduled training date</li>
                    <li>Payment is held in escrow and released to trainer upon successful completion</li>
                    <li>Refund policy: Full refund if cancelled 7+ days before; 50% if 3-7 days; No refund within 3 days</li>
                    <li>Invoices will be generated automatically upon booking confirmation</li>
                  </ul>
                </div>
              </>
            )}

            <h2 className="font-outfit text-lg font-semibold text-slate-900 mt-6 mb-3">
              5. TRAINING CONTRACT
            </h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              For each confirmed training booking, a separate Training Contract will be generated containing:
            </p>
            <ul className="text-slate-600 text-sm space-y-2 list-disc list-inside mt-3">
              <li>Training details (topic, date, time, duration, location)</li>
              <li>Trainer and Corporate information</li>
              <li>Payment amount and terms</li>
              <li>Cancellation and rescheduling policies</li>
              <li>Confidentiality and intellectual property clauses</li>
            </ul>

            <h2 className="font-outfit text-lg font-semibold text-slate-900 mt-6 mb-3">
              6. CANCELLATION POLICY
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-4 py-2 text-left text-slate-700">Cancellation Timeline</th>
                    <th className="px-4 py-2 text-left text-slate-700">Corporate Refund</th>
                    <th className="px-4 py-2 text-left text-slate-700">Trainer Compensation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-slate-200">
                    <td className="px-4 py-2 text-slate-600">7+ days before</td>
                    <td className="px-4 py-2 text-slate-600">100% refund</td>
                    <td className="px-4 py-2 text-slate-600">No compensation</td>
                  </tr>
                  <tr className="border-t border-slate-200 bg-slate-50">
                    <td className="px-4 py-2 text-slate-600">3-7 days before</td>
                    <td className="px-4 py-2 text-slate-600">50% refund</td>
                    <td className="px-4 py-2 text-slate-600">25% of fee</td>
                  </tr>
                  <tr className="border-t border-slate-200">
                    <td className="px-4 py-2 text-slate-600">Less than 3 days</td>
                    <td className="px-4 py-2 text-slate-600">No refund</td>
                    <td className="px-4 py-2 text-slate-600">50% of fee</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="font-outfit text-lg font-semibold text-slate-900 mt-6 mb-3">
              7. INTELLECTUAL PROPERTY
            </h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              All training materials provided by the Trainer remain the intellectual property of the Trainer unless 
              explicitly agreed otherwise in the Training Contract. Corporates receive a non-exclusive license to use 
              materials for internal training purposes only.
            </p>

            <h2 className="font-outfit text-lg font-semibold text-slate-900 mt-6 mb-3">
              8. CONFIDENTIALITY
            </h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              Both parties agree to maintain strict confidentiality of any proprietary information shared during the 
              engagement. This obligation survives the termination of any training contract for a period of 2 years.
            </p>

            <h2 className="font-outfit text-lg font-semibold text-slate-900 mt-6 mb-3">
              9. LIMITATION OF LIABILITY
            </h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              Training Solutions acts as an intermediary platform and shall not be liable for:
            </p>
            <ul className="text-slate-600 text-sm space-y-2 list-disc list-inside mt-3">
              <li>Quality or outcome of training sessions delivered by Trainers</li>
              <li>Any direct, indirect, or consequential damages arising from training engagements</li>
              <li>Disputes between Trainers and Corporates (though we will assist in resolution)</li>
            </ul>
            <p className="text-slate-600 text-sm leading-relaxed mt-3">
              Maximum liability is limited to the platform fee collected for the specific training engagement.
            </p>

            <h2 className="font-outfit text-lg font-semibold text-slate-900 mt-6 mb-3">
              10. DISPUTE RESOLUTION
            </h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              Any disputes shall first be attempted to be resolved through Platform mediation. If unresolved, 
              disputes shall be subject to arbitration under the Arbitration and Conciliation Act, with the 
              seat of arbitration in New Delhi, India.
            </p>

            <h2 className="font-outfit text-lg font-semibold text-slate-900 mt-6 mb-3">
              11. TERMINATION
            </h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              Either party may terminate this Agreement with 30 days written notice. Pending bookings must be 
              honored or cancelled as per the cancellation policy. Platform reserves the right to suspend or 
              terminate accounts for violation of these terms.
            </p>

            <h2 className="font-outfit text-lg font-semibold text-slate-900 mt-6 mb-3">
              12. GOVERNING LAW
            </h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              This Agreement shall be governed by and construed in accordance with the laws of India. 
              Courts in New Delhi shall have exclusive jurisdiction.
            </p>

            <div className="mt-8 pt-6 border-t border-slate-200">
              <p className="text-slate-500 text-xs">
                By clicking "I Accept" during registration, you acknowledge that you have read, understood, 
                and agree to be bound by this Agreement. This electronic acceptance constitutes your legally 
                binding signature.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-8 py-4 bg-slate-50 border-t border-slate-200 flex justify-between items-center">
            <p className="text-xs text-slate-500">
              © 2025 Training Solutions. All rights reserved.
            </p>
            <Button onClick={() => navigate(-1)} variant="outline">
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
